/**
 * Dashboard API — typed wrappers around `/v1/auth/*` and per-user widgets.
 *
 * No new server endpoints — composes hooks from existing modules where they
 * already exist (planned contacts from kontakty, LMS leads from vozidla). Only
 * the `/v1/auth/*` hooks are declared here since the dashboard is the first
 * place they're consumed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface MeUser {
  id: number;
  nick: string;
  jmeno: string;
  prijmeni: string;
  email: string;
  admin: boolean;
  lms_id: number;
  theme_preference?: 'system' | 'light' | 'dark';
  slack_user_id?: string | null;
  slack_enabled?: number;
}

export interface MeMembership {
  Nazev: string;
  /**
   * Server-computed effective levels (access overhaul Stage 2+) — the client
   * permission source of truth; see lib/permissions.ts.
   */
  prava: Record<
    | 'kontakty'
    | 'vozidla'
    | 'ojeta_vozidla'
    | 'leasing'
    | 'zakaznici'
    | 'servis'
    | 'posta'
    | 'kalendar'
    | 'ukoly'
    | 'uskladneni'
    | 'pujcovna',
    'none' | 'read' | 'write' | 'admin'
  >;
  /** Company admin (firmy_uzivatele.spravce). */
  spravce: number;
  /** LMS locations that can sign (names only, main first). */
  lms_pobocky: { id: number; nazev: string }[];
  /** The OTHER firmas of the company's group — targets of "Převést do firmy…" (empty / absent = no group). */
  skupina_firmy?: { id_firmy: number; Nazev: string; ojeta_vozidla?: boolean; vozidla?: boolean }[];
  /** Premier (economy system) is configured for the firma — gates the on-demand Premier section. */
  premier?: boolean;
  /** The firma has set up its servis customer e-mails; without it none is sent. */
  servis_emaily?: boolean;
  slack_enabled?: number;
}

export interface MeResponse {
  user: MeUser;
  company_id: number;
  membership: MeMembership | null;
}

export function useMeQuery() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => api.get<MeResponse>('/v1/auth/me', { signal }),
    retry: false,
    staleTime: 60_000,
  });
}

export interface MyCompany {
  id: number;
  name: string;
}

interface CompaniesResponse {
  companies: MyCompany[];
}

export function useMyCompaniesQuery() {
  return useQuery({
    queryKey: ['auth', 'companies'],
    queryFn: ({ signal }) =>
      api.get<CompaniesResponse>('/v1/auth/companies', { signal }),
    staleTime: 5 * 60_000,
  });
}

interface SelectCompanyResponse {
  status: 'ok';
  company_id: number;
}

export function useSelectCompanyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: number) =>
      api.post<SelectCompanyResponse>('/v1/auth/select-company', {
        company_id: companyId,
      }),
    onSuccess: async () => {
      // Membership + tenant-scoped data must refresh — invalidate everything.
      await qc.invalidateQueries();
    },
  });
}
